import axios from 'axios'
import qs from 'qs'
import type {
  AxiosError,
  AxiosResponse,
  CancelTokenSource,
  Canceler
} from 'axios';
import getSafely from '@/utils/getSafely'
import eventBus from '@/utils/eventBus'
import user from '@/utils/user'
import login from '@/utils/login'
import type { ApiErrorData, RequestOptions } from '@/types/axios'

const CancelToken = axios.CancelToken

const currentLang = localStorage.getItem('lang') || 'zh-CN'
// 默认配置
const instance = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
    'api-version': 'v1',
    'lang': currentLang.replace('-', '_')
  },
  paramsSerializer(params) {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  }
})

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    if (!config.noToken) {
      const Authorization = getSafely(() => {
        const userData = config.temporaryToken ? user.getStagingUserInfo() : user.get()
        const { tokenType, accessToken } = userData
        if (tokenType && accessToken) {
          return tokenType + accessToken
        } else {
          return null
        }
      })
      // 校验是否能取到Authorization
      if (Authorization) {
        config.headers.Authorization = Authorization
      } else {
        const { $cancel } = config
        $cancel()
        login.logout()
        user.remove()
      }
    }

    config.url = config.url.indexOf('http') === 0
      ? config.url
      : process.env.NODE_ENV === 'development'
        ? '/api' + config.url
        : '/webapp' + config.url
    return config
  },
  (error: AxiosError<ApiErrorData>) => Promise.reject(error)
)
instance.interceptors.response.use(
  (responese: AxiosResponse) => responese.data,
  (error: AxiosError<ApiErrorData>) => {
    if (error.name === 'AbortError') {
      return Promise.reject(new Error('请求已取消'))
    }
    const response = error.response
    switch (response?.status) {
      case 401: // token过期
        // 本身就是登录系统，不处理token过期逻辑
        // eventBus.emit('auth:expired');
        break
      case 400:
      case 403:
      case 500:
      case 502:
        eventBus.emit('request:error', {
          status: response?.status,
          error: response?.data?.error,
          errorDescription: response?.data?.errorDescription,
          error_description: response?.data?.error_description
        })
        break
      case 404:
        eventBus.emit('request:404')
        break
      case 423: // 权限被改动
        eventBus.emit('auth:changed');
        break
      default:
        break
    }
    return Promise.reject(error)
  }
)

export default instance

window.axiosCancel = []


export const fetchApi = <T = any>(
  options: RequestOptions,
  executor?: (cancel: Canceler) => void
): Promise<T> => {
  const source: CancelTokenSource = CancelToken.source()
  const cancel: Canceler = source.cancel
  // 合并配置，添加取消令牌和自定义取消函数
  const requestOptions: RequestOptions = {
    ...options,
    cancelToken: source.token,
    $cancel: cancel
  };
  if (executor) {
    executor(cancel)
  }
  return instance(requestOptions)
}

